import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calculator, Car, Ambulance, ShieldCheck, Info, ArrowRight, Loader2,
  MapPin, Calendar, Clock, Repeat, Phone, ExternalLink, ChevronDown,
  CheckCircle2, AlertCircle, HeartHandshake, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Reveal } from '@/components/ui/Reveal';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '@/components/ui/accordion';
import { useSEO, buildBreadcrumb, buildFAQPage, SITE_URL } from '@/lib/seo';
import {
  trackEstimationTransportView, trackTransportTypeSelect,
  trackTransportEstimationComplete, trackTransportContactClick,
  trackAmeliLinkClick, trackPhoneClick,
} from '@/lib/analytics';
import api from '@/lib/api';
import { parseApiError } from '@/lib/parseApiError';
import { toast } from 'sonner';

// ============ CONFIG ============
const CANONICAL = `${SITE_URL}/estimation-transport`;
const AMELI_URL = 'https://www.ameli.fr/assure/remboursements/rembourse/transports/transports';
const TRANSPORT_TYPES = [
  {
    key: 'taxi', label: 'Taxi',
    icon: Car,
    tagline: 'Taxi conventionné (Allier)',
    color: 'from-amber-400 to-amber-600',
    borderActive: 'border-amber-500 ring-amber-200',
    subtitle: 'Prise en charge, tarifs préfectoraux',
  },
  {
    key: 'vsl', label: 'VSL',
    icon: Ambulance, // proxy visuel (VSL = véhicule sanitaire léger, siège)
    tagline: 'Véhicule Sanitaire Léger',
    color: 'from-brand-500 to-brand-700',
    borderActive: 'border-brand-500 ring-brand-200',
    subtitle: 'Barème conventionnel majoré',
  },
  {
    key: 'ambulance', label: 'Ambulance',
    icon: HeartHandshake,
    tagline: 'Ambulance conventionnée',
    color: 'from-brand-700 to-brand-900',
    borderActive: 'border-brand-700 ring-brand-200',
    subtitle: 'Barème conventionnel majoré',
  },
];

const FAQS = [
  { q: "L'estimation obtenue est-elle un devis contractuel ?", a: "Non. Le montant affiché est une estimation indicative et non contractuelle basée sur les barèmes conventionnels ou préfectoraux en vigueur. Le montant réel peut varier selon les conditions du transport (temps d'attente, suppléments, aléas de circulation)." },
  { q: "Quels sont les tarifs officiels VSL et Ambulance ?", a: "Nous appliquons les barèmes conventionnels de l'Assurance Maladie : un forfait départemental (15,75 € pour un VSL, 57,39 € pour une ambulance), auquel s'ajoute un tarif kilométrique (1,10 €/km pour un VSL, 2,44 €/km pour une ambulance) au-delà des 3 premiers kilomètres. Des majorations réglementaires s'appliquent la nuit, le dimanche et les jours fériés." },
  { q: "Mon transport peut-il être pris en charge par l'Assurance Maladie ?", a: "Oui, selon votre situation médicale et administrative. La prise en charge dépend d'une prescription médicale de transport adaptée. Certains transports peuvent nécessiter un accord préalable. Consultez le site officiel Ameli pour connaître vos droits." },
  { q: "Comment est calculée la distance ?", a: "Nous utilisons la distance routière calculée automatiquement à partir des adresses saisies (via OpenStreetMap). Si le calcul automatique échoue, vous pouvez indiquer manuellement la distance estimée du trajet. La distance à vol d'oiseau n'est jamais utilisée pour la tarification." },
  { q: "Que se passe-t-il pour un aller-retour ?", a: "Pour les transports VSL et ambulance, l'aller-retour est facturé selon la distance totale parcourue (distance × 2) en appliquant les règles conventionnelles habituelles. Pour un taxi, un tarif spécifique est prévu." },
  { q: "Quelle est la différence entre un VSL et une ambulance ?", a: "L'ambulance est utilisée lorsque le patient doit être transporté en position allongée ou nécessite une surveillance médicale. Le VSL (Véhicule Sanitaire Léger) est adapté aux patients pouvant voyager en position assise mais nécessitant une assistance. Le choix est indiqué par votre médecin sur la prescription." },
];

// ============ COMPONENT ============
export default function EstimationTransport() {
  useSEO({
    title: "Prix Taxi, VSL et Ambulance dans l'Allier | Simulateur",
    description: "Estimez le prix de votre trajet en Taxi, VSL ou Ambulance dans l'Allier. Simulation indicative et informations sur la prise en charge Assurance Maladie.",
    keywords: "prix taxi allier, tarif taxi allier, estimation taxi allier, prix vsl, tarif vsl, prix transport sanitaire, tarif ambulance, prix ambulance, estimation transport sanitaire, prix transport médical, calcul prix vsl, calcul prix ambulance, simulateur transport allier",
    canonical: CANONICAL,
    ogType: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: "Prix Taxi, VSL et Ambulance dans l'Allier | Simulateur",
        url: CANONICAL,
        inLanguage: 'fr-FR',
        isPartOf: { '@type': 'WebSite', name: 'Groupe Ambulances Beaulon', url: SITE_URL },
        about: [
          { '@type': 'Service', name: 'Estimation prix taxi conventionné' },
          { '@type': 'Service', name: 'Estimation prix VSL' },
          { '@type': 'Service', name: 'Estimation prix ambulance' },
        ],
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'Estimation de transport', item: CANONICAL },
          ],
        },
      },
      buildBreadcrumb([
        { name: 'Accueil', url: SITE_URL + '/' },
        { name: 'Estimation de transport', url: CANONICAL },
      ]),
      buildFAQPage(FAQS),
    ],
  });

  const [type, setType] = useState(null); // 'taxi' | 'vsl' | 'ambulance'
  const [step, setStep] = useState(1); // 1 = form, 2 = result

  // Form state
  const [addressFrom, setAddressFrom] = useState('');
  const [addressTo, setAddressTo] = useState('');
  const [fromCoords, setFromCoords] = useState(null);   // {lat,lng}
  const [toCoords, setToCoords] = useState(null);
  const [distance, setDistance] = useState('');
  const [manualDistance, setManualDistance] = useState(false); // true = user overrides
  const [routeError, setRouteError] = useState(null);
  const [date, setDate] = useState('');
  const [heure, setHeure] = useState('');
  const [allerRetour, setAllerRetour] = useState(false);
  const [computingRoute, setComputingRoute] = useState(false);

  // Result state
  const [result, setResult] = useState(null);
  const [nearest, setNearest] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  const resultRef = useRef(null);

  // View tracking
  useEffect(() => {
    trackEstimationTransportView();
  }, []);

  // Auto route calculation when both coords are set (unless user manually overrode)
  useEffect(() => {
    if (fromCoords && toCoords && !manualDistance) {
      calcRoute(fromCoords, toCoords);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromCoords, toCoords]);

  const activeTypeCfg = useMemo(
    () => TRANSPORT_TYPES.find(t => t.key === type),
    [type]
  );

  const derivedTimeFlags = useMemo(() => {
    // Determine is_night / is_sunday_holiday from date+heure
    if (!date || !heure) return { isNight: false, isSundayHoliday: false, unknown: true };
    try {
      const dt = new Date(`${date}T${heure}`);
      if (Number.isNaN(dt.getTime())) return { isNight: false, isSundayHoliday: false, unknown: true };
      const hh = dt.getHours();
      const day = dt.getDay(); // 0 = Sunday
      const isNight = hh < 8 || hh >= 20;
      // Règle conventionnelle : tarif dimanche démarre samedi 12h.
      let isSundayHoliday = day === 0;
      if (day === 6 && hh >= 12) isSundayHoliday = true;
      return { isNight, isSundayHoliday, unknown: false };
    } catch (e) {
      return { isNight: false, isSundayHoliday: false, unknown: true };
    }
  }, [date, heure]);

  const selectType = (t) => {
    setType(t);
    trackTransportTypeSelect(t);
    // Reset result when changing type
    setResult(null);
    setStep(1);
  };

  const suggestFromTimer = useRef(null);
  const suggestToTimer = useRef(null);

  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);

  const debouncedGeocode = (rawValue, timerRef, setSuggestions) => {
    // Normalisation défensive : trim + espaces multiples réduits
    // (Nominatim est déjà insensible à la casse, on garantit ici la robustesse UX)
    const value = (rawValue || '').trim().replace(/\s+/g, ' ');
    if (value.length < 3) {
      setSuggestions([]);
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/geocode?q=${encodeURIComponent(value)}&limit=5`);
        setSuggestions(res.data || []);
      } catch (e) {
        setSuggestions([]);
      }
    }, 350);
  };

  const handleFromChange = (v) => {
    setAddressFrom(v);
    setFromCoords(null);
    debouncedGeocode(v, suggestFromTimer, setFromSuggestions);
  };
  const handleToChange = (v) => {
    setAddressTo(v);
    setToCoords(null);
    debouncedGeocode(v, suggestToTimer, setToSuggestions);
  };

  const pickFrom = (s) => {
    setAddressFrom(s.display_name);
    setFromCoords({ lat: s.lat, lng: s.lng });
    setFromSuggestions([]);
  };
  const pickTo = (s) => {
    setAddressTo(s.display_name);
    setToCoords({ lat: s.lat, lng: s.lng });
    setToSuggestions([]);
  };

  const calcRoute = async (from, to) => {
    setComputingRoute(true);
    setRouteError(null);
    try {
      const res = await api.get(
        `/route-distance?from_lat=${from.lat}&from_lng=${from.lng}&to_lat=${to.lat}&to_lng=${to.lng}`
      );
      if (res.data?.distance_km) {
        setDistance(String(res.data.distance_km));
      }
    } catch (e) {
      setRouteError("Distance automatique indisponible — saisissez-la manuellement.");
      setManualDistance(true);
    } finally {
      setComputingRoute(false);
    }
  };

  const canSubmit = () => {
    const d = parseFloat(distance);
    return type && d > 0 && d <= 1000 && date && heure;
  };

  const submit = async () => {
    if (!canSubmit()) {
      toast.error("Merci de renseigner la distance, la date et l'heure.");
      return;
    }
    const d = parseFloat(distance);
    setLoading(true);
    try {
      const body = {
        distance_km: d,
        is_night: derivedTimeFlags.isNight,
        is_sunday_holiday: derivedTimeFlags.isSundayHoliday,
        aller_retour: allerRetour,
      };
      let res;
      if (type === 'taxi') {
        res = await api.post('/taxi-estimate', body);
      } else {
        res = await api.post('/transport-estimate', { transport_type: type, ...body });
      }
      setResult(res.data);
      trackTransportEstimationComplete(type, d, res.data.estimation_total);

      // Fetch nearest implantation if we have origin coords
      if (fromCoords) {
        try {
          const n = await api.post('/nearest-implantation', fromCoords);
          setNearest(n.data);
        } catch (e) { setNearest(null); }
      }

      setStep(2);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err) {
      const msg = parseApiError(err, "Erreur de calcul. Merci de réessayer ou de nous contacter directement.");
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const reset = () => {
    setType(null); setStep(1); setResult(null); setNearest(null);
    setAddressFrom(''); setAddressTo(''); setFromCoords(null); setToCoords(null);
    setDistance(''); setManualDistance(false); setRouteError(null);
    setDate(''); setHeure(''); setAllerRetour(false); setShowDetail(false);
  };

  return (
    <div>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-brand-50/40 to-white">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-brand-100/30 blur-3xl pointer-events-none"></div>
        <div className="container-app relative pt-10 sm:pt-14 pb-8 lg:pb-12">
          <Reveal>
            <p className="badge-soft mb-3"><Sparkles className="h-3.5 w-3.5" /> Simulateur</p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="h1 text-anthracite-950 max-w-3xl" data-testid="estimation-transport-h1">
              Estimez le prix de votre transport dans l'Allier
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="lead mt-4 max-w-2xl">
              Taxi, VSL ou Ambulance : obtenez une estimation indicative de votre trajet en quelques secondes.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ CHOIX TYPE ============ */}
      <section className="section-pad-sm bg-white">
        <div className="container-app">
          <Reveal>
            <p className="text-sm font-medium text-anthracite-700 mb-4">1. Choisissez votre type de transport</p>
          </Reveal>
          <div className="grid gap-3 sm:grid-cols-3">
            {TRANSPORT_TYPES.map((t, i) => {
              const IconComp = t.icon;
              const active = type === t.key;
              return (
                <Reveal key={t.key} delay={i * 60}>
                  <button
                    type="button"
                    onClick={() => selectType(t.key)}
                    className={`w-full text-left card-base p-5 transition-all border-2 ${active ? t.borderActive + ' ring-4' : 'border-border hover:border-brand-300'}`}
                    data-testid={`estimation-transport-type-${t.key}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${t.color} text-white flex items-center justify-center shrink-0`}>
                        <IconComp className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-heading font-semibold text-anthracite-950">{t.label}</h3>
                        <p className="mt-1 text-xs text-muted-foreground">{t.tagline}</p>
                        <p className="mt-2 text-[11px] text-anthracite-700 uppercase tracking-wide">{t.subtitle}</p>
                      </div>
                      {active && (
                        <div className="ml-auto shrink-0"><CheckCircle2 className="h-5 w-5 text-brand-700" /></div>
                      )}
                    </div>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ FORMULAIRE ============ */}
      {type && (
        <section className="section-pad-sm bg-white" id="simulateur">
          <div className="container-app max-w-3xl">
            <Reveal>
              <p className="text-sm font-medium text-anthracite-700 mb-4">2. Renseignez votre trajet</p>
            </Reveal>
            <div className="card-base p-6 sm:p-8" data-testid="estimation-transport-form">
              <div className="grid gap-4">
                {/* Adresses */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <AddressField
                    label="Adresse ou commune de départ *"
                    value={addressFrom}
                    onChange={handleFromChange}
                    suggestions={fromSuggestions}
                    onPick={pickFrom}
                    testid="estimation-transport-from"
                    placeholder="Ex : Moulins, 03000"
                  />
                  <AddressField
                    label="Adresse ou commune de destination *"
                    value={addressTo}
                    onChange={handleToChange}
                    suggestions={toSuggestions}
                    onPick={pickTo}
                    testid="estimation-transport-to"
                    placeholder="Ex : CHU Clermont-Ferrand"
                  />
                </div>

                {/* Distance auto/manuelle */}
                <div>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <Label className="text-sm font-medium">Distance routière estimée (km) *</Label>
                    {computingRoute && (
                      <span className="text-xs text-brand-700 inline-flex items-center gap-1.5">
                        <Loader2 className="h-3 w-3 animate-spin" /> Calcul de la distance…
                      </span>
                    )}
                    {!computingRoute && fromCoords && toCoords && !manualDistance && distance && !routeError && (
                      <span className="text-xs text-emerald-700 inline-flex items-center gap-1.5">
                        <CheckCircle2 className="h-3 w-3" /> Distance routière détectée
                      </span>
                    )}
                  </div>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={distance}
                    onChange={(e) => { setDistance(e.target.value); setManualDistance(true); }}
                    className="mt-2"
                    placeholder="Ex : 12"
                    data-testid="estimation-transport-distance"
                  />
                  {routeError && (
                    <p className="mt-1.5 text-xs text-amber-700 inline-flex items-center gap-1.5">
                      <AlertCircle className="h-3 w-3" /> {routeError}
                    </p>
                  )}
                  {!routeError && (
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      Distance calculée automatiquement à partir des adresses. Vous pouvez la modifier manuellement.
                    </p>
                  )}
                </div>

                {/* Date + Heure */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Date *</Label>
                    <Input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="mt-2"
                      data-testid="estimation-transport-date"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Heure *</Label>
                    <Input
                      type="time"
                      value={heure}
                      onChange={(e) => setHeure(e.target.value)}
                      className="mt-2"
                      data-testid="estimation-transport-heure"
                    />
                  </div>
                </div>

                {/* Options */}
                <label className="flex items-center justify-between gap-3 rounded-lg border p-3 cursor-pointer">
                  <span className="flex items-center gap-2 text-sm">
                    <Repeat className="h-4 w-4 text-brand-700" /> Aller-retour
                  </span>
                  <Switch
                    checked={allerRetour}
                    onCheckedChange={setAllerRetour}
                    data-testid="estimation-transport-ar"
                  />
                </label>

                {/* Indicateur des majorations détectées */}
                {(derivedTimeFlags.isNight || derivedTimeFlags.isSundayHoliday) && (
                  <div className="rounded-lg bg-brand-50 border border-brand-100 p-3 text-xs text-anthracite-800">
                    <p className="font-medium text-brand-800 inline-flex items-center gap-1.5">
                      <Info className="h-3.5 w-3.5" /> Majorations réglementaires détectées :
                    </p>
                    <ul className="mt-1 ml-5 list-disc space-y-0.5">
                      {derivedTimeFlags.isNight && <li>Transport de nuit (20h – 8h)</li>}
                      {derivedTimeFlags.isSundayHoliday && <li>Dimanche / jour férié (règle samedi 12h)</li>}
                    </ul>
                  </div>
                )}

                <Button
                  onClick={submit}
                  disabled={loading || !canSubmit()}
                  size="lg"
                  className="w-full mt-2"
                  data-testid="estimation-transport-submit"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Calcul en cours…</>
                  ) : (
                    <><Calculator className="h-4 w-4" /> Calculer mon estimation <ArrowRight className="h-4 w-4" /></>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ RESULTAT ============ */}
      {result && step === 2 && (
        <section ref={resultRef} className="section-pad-sm bg-gradient-to-b from-brand-50/40 to-white scroll-mt-24">
          <div className="container-app max-w-3xl">
            <Reveal>
              <div className="card-base p-6 sm:p-8 border-brand-200 bg-white" data-testid="estimation-transport-result">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-xs uppercase tracking-[0.14em] text-brand-700 font-semibold">
                      Estimation de votre transport en {activeTypeCfg?.label}
                    </p>
                    <p className="mt-3 text-5xl sm:text-6xl font-heading font-extrabold text-anthracite-950">
                      Environ {(result.estimation_total || 0).toFixed(2)} €
                    </p>
                    <p className="mt-3 text-sm text-anthracite-700">
                      Estimation indicative avant éventuelle prise en charge par l'Assurance Maladie.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={reset}
                    className="text-sm text-brand-700 hover:text-brand-900 underline"
                    data-testid="estimation-transport-reset"
                  >
                    Nouvelle simulation
                  </button>
                </div>

                {/* Détails optionnels */}
                <div className="mt-6 border-t pt-4">
                  <button
                    type="button"
                    onClick={() => setShowDetail(!showDetail)}
                    className="text-sm font-medium text-anthracite-800 inline-flex items-center gap-1.5 hover:text-brand-700"
                    data-testid="estimation-transport-toggle-detail"
                  >
                    {showDetail ? 'Masquer le détail' : "Voir le détail de l'estimation"}
                    <ChevronDown className={`h-4 w-4 transition-transform ${showDetail ? 'rotate-180' : ''}`} />
                  </button>
                  {showDetail && (
                    <div className="mt-4 space-y-4 text-sm">
                      {type === 'taxi' ? (
                        <div className="grid sm:grid-cols-2 gap-3">
                          <DetailRow label="Distance saisie" value={`${result.distance_km} km`} />
                          <DetailRow label="Distance totale facturée" value={`${result.total_km} km`} />
                          <DetailRow label="Prise en charge" value={`${(result.prise_en_charge || 0).toFixed(2)} €`} />
                          <DetailRow label="Tarif kilométrique" value={`${(result.tarif_kilometrique || 0).toFixed(2)} €/km`} />
                          <DetailRow label="Coût kilométrique" value={`${(result.cout_kilometrique || 0).toFixed(2)} €`} />
                          <DetailRow label="Minimum de course" value={`${(result.minimum_course || 0).toFixed(2)} €`} />
                          <DetailRow label="Tarif appliqué" value={result.tarif_label} colSpan />
                        </div>
                      ) : (
                        <>
                          {/* Récapitulatif global */}
                          <div className="grid sm:grid-cols-2 gap-3">
                            <DetailRow label="Distance saisie" value={`${result.distance_km} km`} />
                            <DetailRow label="Nombre de trajets" value={result.aller_retour ? "2 (aller + retour)" : "1 (aller simple)"} />
                            <DetailRow label={`Forfait départemental${result.aller_retour ? ' (×2)' : ''}`} value={`${(result.forfait_departemental || 0).toFixed(2)} €${result.aller_retour ? ` (2 × ${(result.forfait_par_trajet || 0).toFixed(2)})` : ''}`} colSpan />
                            <DetailRow label="Km supplémentaires" value={`${result.km_supplementaires} km`} />
                            <DetailRow label="Tarif kilométrique" value={`${(result.tarif_kilometrique || 0).toFixed(2)} €/km`} />
                            <DetailRow label="Coût kilométrique total" value={`${(result.cout_kilometrique || 0).toFixed(2)} €`} />
                            <DetailRow label="Total avant majoration" value={`${(result.total_tarif_jour || 0).toFixed(2)} €`} />
                            <DetailRow label="Majoration" value={`${result.majoration_label}${result.majoration_pourcentage != null ? ` (${result.majoration_pourcentage}%)` : ''}`} colSpan />
                            {result.montant_majoration > 0 && (
                              <DetailRow label="Montant de la majoration" value={`+ ${(result.montant_majoration || 0).toFixed(2)} €`} colSpan />
                            )}
                          </div>

                          {/* Détail trajet par trajet (uniquement A/R) */}
                          {result.aller_retour && result.trajets && result.trajets.length === 2 && (
                            <div className="rounded-lg bg-anthracite-100/40 border p-4">
                              <p className="text-xs uppercase tracking-[0.14em] text-brand-700 font-semibold mb-3">
                                Détail par trajet
                              </p>
                              <div className="grid sm:grid-cols-2 gap-4">
                                {result.trajets.map((t) => (
                                  <div key={t.trajet_label} className="rounded-lg bg-white border p-3">
                                    <p className="font-heading font-semibold text-anthracite-950 text-sm">{t.trajet_label}</p>
                                    <ul className="mt-2 space-y-1 text-xs text-anthracite-800">
                                      <li className="flex justify-between"><span className="text-muted-foreground">Forfait</span><span>{(t.forfait || 0).toFixed(2)} €</span></li>
                                      <li className="flex justify-between"><span className="text-muted-foreground">Km facturés ({t.km_supplementaires} km × {(t.tarif_kilometrique || 0).toFixed(2)})</span><span>{(t.cout_kilometrique || 0).toFixed(2)} €</span></li>
                                      {t.montant_majoration > 0 && (
                                        <li className="flex justify-between"><span className="text-muted-foreground">Majoration ({t.majoration_pourcentage}%)</span><span>+ {(t.montant_majoration || 0).toFixed(2)} €</span></li>
                                      )}
                                      <li className="flex justify-between font-semibold text-anthracite-950 border-t pt-1 mt-1"><span>Total {t.trajet_label.toLowerCase()}</span><span>{(t.total_trajet || 0).toFixed(2)} €</span></li>
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <p className="mt-6 text-[11px] text-muted-foreground italic">
                  {result.mention || "Estimation indicative et non contractuelle. Le montant réel peut varier selon les conditions du transport et la réglementation applicable."}
                </p>
              </div>
            </Reveal>

            {/* Bloc Assurance Maladie */}
            <Reveal delay={80}>
              <div className="mt-6 card-base p-5 sm:p-6 border-brand-200 bg-brand-50/40" data-testid="estimation-transport-ameli">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-brand-500 text-white flex items-center justify-center shrink-0">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-anthracite-950">
                      Votre transport peut éventuellement être pris en charge
                    </h3>
                    <p className="mt-2 text-sm text-anthracite-800 leading-relaxed">
                      {result.mention_assurance_maladie || "Selon votre situation médicale et administrative, votre transport peut être pris en charge totalement ou partiellement par l'Assurance Maladie. Cette prise en charge dépend notamment des conditions applicables à votre situation et d'une prescription médicale de transport adaptée. Certains transports peuvent également nécessiter un accord préalable."}
                    </p>
                    <a
                      href={result.url_ameli || AMELI_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackAmeliLinkClick('estimation_transport_result')}
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:text-brand-900"
                      data-testid="estimation-transport-ameli-link"
                    >
                      Comprendre la prise en charge <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Bloc Contact */}
            <Reveal delay={160}>
              <div className="mt-6 card-base p-5 sm:p-6" data-testid="estimation-transport-contact">
                <h3 className="font-heading font-semibold text-anthracite-950 text-lg">
                  Besoin d'organiser ce transport ?
                </h3>
                <p className="mt-2 text-sm text-anthracite-700">
                  Notre équipe locale vous répond rapidement. Choisissez la manière la plus pratique :
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <Link
                    to={buildContactURL({ type, addressFrom, addressTo, distance, date, heure, allerRetour, estimation: result.estimation_total, target: 'info' })}
                    onClick={() => trackTransportContactClick(type, 'inline_form')}
                    className="rounded-xl bg-brand-500 hover:bg-brand-600 text-white px-4 py-3 text-sm font-medium inline-flex items-center gap-2 justify-center transition-colors"
                    data-testid="estimation-transport-cta-info"
                  >
                    Demander des informations <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to={buildContactURL({ type, addressFrom, addressTo, distance, date, heure, allerRetour, estimation: result.estimation_total, target: 'callback' })}
                    onClick={() => trackTransportContactClick(type, 'callback')}
                    className="rounded-xl bg-white border-2 border-brand-500 text-brand-700 hover:bg-brand-50 px-4 py-3 text-sm font-medium inline-flex items-center gap-2 justify-center transition-colors"
                    data-testid="estimation-transport-cta-callback"
                  >
                    Être rappelé <Phone className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/implantations"
                    onClick={() => trackTransportContactClick(type, 'landing')}
                    className="rounded-xl bg-white border border-border hover:bg-anthracite-100/50 px-4 py-3 text-sm font-medium inline-flex items-center gap-2 justify-center transition-colors"
                    data-testid="estimation-transport-cta-implantation"
                  >
                    Voir les 4 implantations <MapPin className="h-4 w-4" />
                  </Link>
                </div>

                {/* Suggestion implantation la plus proche */}
                {nearest && (
                  <div className="mt-5 border-t pt-4">
                    <p className="text-xs uppercase tracking-[0.14em] text-brand-700 font-semibold">
                      Implantation la plus proche de votre départ
                    </p>
                    <div className="mt-3 rounded-lg bg-anthracite-100/40 border p-4 flex flex-wrap items-center gap-4">
                      <div className="min-w-0">
                        <p className="font-heading font-semibold text-anthracite-950">{nearest.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{nearest.zone_label} · à environ {nearest.distance_haversine_km} km à vol d'oiseau</p>
                      </div>
                      <div className="ml-auto flex gap-2 flex-wrap">
                        <a
                          href={`tel:${nearest.phone_tel}`}
                          onClick={() => { trackPhoneClick(`estimation-transport-nearest-${nearest.slug}`, nearest.phone_display); trackTransportContactClick(type, 'phone'); }}
                          className="inline-flex items-center gap-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white px-3 py-2 text-sm font-medium"
                          data-testid="estimation-transport-nearest-call"
                        >
                          <Phone className="h-4 w-4" /> {nearest.phone_display}
                        </a>
                        <Link
                          to={nearest.landing_path}
                          onClick={() => trackTransportContactClick(type, 'landing')}
                          className="inline-flex items-center gap-2 rounded-lg bg-white border text-anthracite-900 px-3 py-2 text-sm font-medium hover:bg-anthracite-100/50"
                          data-testid="estimation-transport-nearest-landing"
                        >
                          Voir la page dédiée <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      L'implantation la plus proche est indiquée à titre d'information. Elle ne garantit pas la disponibilité immédiate d'un véhicule.
                    </p>
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ============ IMPLANTATIONS (toujours visibles bas de page) ============ */}
      <section className="section-pad-sm bg-anthracite-100/40">
        <div className="container-app max-w-5xl">
          <Reveal>
            <p className="badge-soft mb-3">Nos implantations</p>
            <h2 className="h3 text-anthracite-950">Une équipe locale près de chez vous</h2>
          </Reveal>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {STATIC_IMPLANTATIONS.map((imp, i) => (
              <Reveal key={imp.slug} delay={i * 60}>
                <div className="card-base p-4 h-full flex flex-col" data-testid={`estimation-transport-impl-${imp.slug}`}>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-brand-700 shrink-0 mt-0.5" />
                    <p className="font-heading font-semibold text-anthracite-950 text-sm leading-tight">{imp.name}</p>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{imp.zone_label}</p>
                  <div className="mt-auto pt-3 flex flex-col gap-2">
                    <a
                      href={`tel:${imp.phone_tel}`}
                      onClick={() => trackPhoneClick(`estimation-transport-impl-${imp.slug}`, imp.phone_display)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:text-brand-900"
                    >
                      <Phone className="h-3.5 w-3.5" /> {imp.phone_display}
                    </a>
                    <Link
                      to={imp.landing_path}
                      className="text-xs font-medium text-brand-700 hover:text-brand-900 inline-flex items-center gap-1"
                    >
                      Page dédiée <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="section-pad-sm bg-white">
        <div className="container-app max-w-3xl">
          <Reveal>
            <p className="badge-soft mb-3">FAQ</p>
            <h2 className="h3 text-anthracite-950">Questions fréquentes sur l'estimation</h2>
          </Reveal>
          <Reveal delay={80}>
            <Accordion type="single" collapsible className="mt-6" data-testid="estimation-transport-faq">
              {FAQS.map((item, i) => (
                <AccordionItem key={item.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-heading font-semibold text-anthracite-950 hover:text-brand-700">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-anthracite-700 leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={140}>
            <div className="mt-6 rounded-xl bg-anthracite-100/60 border p-4 text-sm text-anthracite-800 flex gap-3">
              <Info className="h-5 w-5 text-brand-700 shrink-0" />
              <p>
                Vous cherchez uniquement une estimation Taxi ?{' '}
                <Link to="/estimation-taxi" className="font-medium text-brand-700 hover:text-brand-900 underline">
                  Utilisez notre simulateur Taxi dédié
                </Link>
                {' '}pour un calcul détaillé selon les tarifs préfectoraux de l'Allier.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

// ============ HELPERS ============
function DetailRow({ label, value, colSpan }) {
  return (
    <div className={`flex items-center justify-between border-b border-anthracite-200/50 pb-2 ${colSpan ? 'sm:col-span-2' : ''}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-anthracite-900">{value}</span>
    </div>
  );
}

function AddressField({ label, value, onChange, suggestions, onPick, testid, placeholder }) {
  return (
    <div className="relative">
      <Label className="text-sm font-medium">{label}</Label>
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2"
        autoComplete="off"
        data-testid={testid}
      />
      {suggestions && suggestions.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full rounded-lg border bg-white shadow-lg max-h-56 overflow-y-auto">
          {suggestions.map((s, i) => (
            <li key={`${s.display_name}-${i}`}>
              <button
                type="button"
                onClick={() => onPick(s)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-brand-50 border-b last:border-b-0"
                data-testid={`${testid}-suggestion-${i}`}
              >
                <span className="line-clamp-2">{s.display_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Pre-fills contact URL with simulation data
function buildContactURL({ type, addressFrom, addressTo, distance, date, heure, allerRetour, estimation, target }) {
  const params = new URLSearchParams();
  params.set('source', 'estimation_transport');
  if (type) params.set('type_transport', type);
  if (addressFrom) params.set('commune_depart', addressFrom.split(',')[0].trim());
  if (addressTo) params.set('destination', addressTo.split(',')[0].trim());
  if (distance) params.set('distance_km', distance);
  if (date) params.set('date', date);
  if (heure) params.set('heure', heure);
  if (allerRetour) params.set('aller_retour', '1');
  if (estimation != null) params.set('estimation', String(estimation.toFixed ? estimation.toFixed(2) : estimation));
  if (target === 'callback') params.set('callback', '1');
  // Route toward /contact (existing page)
  return `/contact?${params.toString()}`;
}

// Duplicated 4 implantations metadata for the always-visible block (same source
// of truth server-side in transport_rates.py). Kept minimal to avoid extra API
// calls on page load; changes must be synced with /app/backend/transport_rates.py.
const STATIC_IMPLANTATIONS = [
  { slug: 'ambulance-beaulon-garnat', name: 'Ambulances Beaulon – Garnat-sur-Engièvre', zone_label: 'Garnat / Beaulon / nord-est Allier', phone_display: '04 70 42 72 63', phone_tel: '+33470427263', landing_path: '/ambulance-beaulon-garnat' },
  { slug: 'ambulance-03-avermes', name: 'Ambulance 03 – Avermes / Saint-Ennemond', zone_label: 'Avermes / Moulins / Saint-Ennemond', phone_display: '06 56 66 05 71', phone_tel: '+33656660571', landing_path: '/ambulance-03-avermes' },
  { slug: 'celine-ambulance-diou', name: 'Céline Ambulance – Diou', zone_label: 'Diou / Val de Besbre', phone_display: '04 70 42 90 08', phone_tel: '+33470429008', landing_path: '/celine-ambulance-diou' },
  { slug: 'ambulance-tronget-le-montet', name: 'Ambulance Tronget – Le Montet', zone_label: 'Tronget / Le Montet / centre-sud Allier', phone_display: '04 70 47 10 30', phone_tel: '+33470471030', landing_path: '/ambulance-tronget-le-montet' },
];
